import React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import { isArrowKey, Keys } from "@digits-shared/components/UI/Elements/Keys"
import { Input } from "@digits-shared/DesignSystem/FormElements/Input"
import {
  COMMON_SELECT_ITEM_STYLES,
  COMMON_SELECT_TRIGGER_STYLES,
  SELECT_CHEVRON_WRAPPER_STYLES,
  SELECT_TRIGGER_CLASSNAME,
  SelectInputContainer,
  SelectWrapper,
} from "@digits-shared/DesignSystem/FormElements/Selects/common"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@digits-shared/DesignSystem/Popovers/Popover"
import numberHelper from "@digits-shared/helpers/numberHelper"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import colors from "@digits-shared/themes/colors"
import { DetailText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { SVGIconComponent } from "src/shared/components/Icons/SVGIcon"

const ICONS_PER_ROW = 5
const ICON_GRID_GAP = 4

const NoResultsMessage = styled(DetailText)`
  width: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  color: ${colors.secondary70};
`

const Icon = styled(SVGIconComponent)`
  width: 24px;
  height: 24px;
  ${svgPathStyles(colors.secondary, 1.5)};

  &.placeholder {
    ${svgPathStyles(colors.secondary50, 1.5)};
  }
`

const ValueWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
`

const IconPopoverTrigger = styled(PopoverTrigger)<{ $invalid?: boolean }>`
  ${COMMON_SELECT_TRIGGER_STYLES};
`

const IconPopoverContent = styled(PopoverContent)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 0;
`

const IconWrapper = styled.div<{ $open: boolean }>`
  ${SELECT_CHEVRON_WRAPPER_STYLES};
  flex-shrink: 0;
`

const IconGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(${ICONS_PER_ROW}, 40px);
  align-items: center;
  justify-content: center;
  gap: ${ICON_GRID_GAP}px;
  max-height: 200px;
  overflow-y: auto;
  margin-right: -8px;
  padding: 0 8px 8px 0;
`

const StyledIconItem = styled.li`
  ${COMMON_SELECT_ITEM_STYLES};
  padding: 0 !important;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &[aria-selected="true"] {
    background: ${colors.glowHighlight};
  }

  &[aria-current="true"] {
    border: 1px solid ${colors.accentBlue};
  }
`

export type IconConfig = { displayKey: string; searchTerms: string[] }

interface IconSelectProps {
  id?: string
  ariaLabel?: string
  placeholderDisplayKey?: string
  activeDisplayKey?: string
  onSelect: (displayKey: string) => void
  invalid?: boolean
  fullWidth?: boolean
  className?: string
  icons: IconConfig[]
  disabled?: boolean
  required?: boolean
}

interface IconOptionProps {
  displayKey: string
  onIconSelect: (displayKey: string) => void
  selected: boolean
  current: boolean
}

export const IconSelect = React.forwardRef<HTMLButtonElement, IconSelectProps>(
  (
    {
      fullWidth,
      placeholderDisplayKey = "Tag01",
      ariaLabel = "Icon select",
      id,
      activeDisplayKey,
      invalid,
      icons,
      onSelect,
      disabled,
      className,
      required,
    },
    forwardedRef
  ) => {
    const [activeIndex, setActiveIndex] = React.useState(-1)
    const [isOpen, setIsOpen] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState("")
    const { value: didBlur, setTrue: setDidBlur } = useStateBoolean()
    const isInvalid = invalid || (required && !activeDisplayKey && didBlur && !disabled)

    const onOpenChange = React.useCallback(
      (open: boolean) => {
        setIsOpen(open)

        if (!open) {
          setSearchTerm("")
          setActiveIndex(-1)
          setDidBlur()
        }
      },
      [setDidBlur]
    )

    const onIconSelect = React.useCallback(
      (displayKey?: string) => {
        if (displayKey) {
          onSelect(displayKey)
        }

        setIsOpen(false)
        setSearchTerm("")
        setActiveIndex(-1)
        setDidBlur()
      },
      [onSelect, setDidBlur]
    )

    const searchableIcons = React.useMemo(
      () =>
        icons.map((icon) => ({ ...icon, searchTerms: icon.searchTerms.join(" ").toLowerCase() })),
      [icons]
    )

    const filteredIcons = React.useMemo(
      () =>
        searchTerm
          ? searchableIcons.filter(({ searchTerms }) =>
              searchTerms.includes(searchTerm.toLowerCase())
            )
          : searchableIcons,
      [searchTerm, searchableIcons]
    )

    const onKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (activeIndex === -1 && isArrowKey(e.key)) {
          setActiveIndex(0)
          return
        }

        let newIndex = activeIndex

        switch (e.key) {
          case Keys.Enter: {
            onIconSelect(filteredIcons[activeIndex]?.displayKey)
            return
          }
          case Keys.ArrowDown:
            newIndex = activeIndex + ICONS_PER_ROW
            break
          case Keys.ArrowUp:
            newIndex = activeIndex - ICONS_PER_ROW
            break
          case Keys.ArrowRight:
            newIndex = activeIndex + 1
            break
          case Keys.ArrowLeft:
            newIndex = activeIndex - 1
            break
        }

        setActiveIndex(numberHelper.clamp(newIndex, 0, filteredIcons.length - 1))
      },

      [activeIndex, filteredIcons, onIconSelect]
    )

    const onInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
      setActiveIndex(0)
    }, [])

    React.useEffect(() => {
      if (!searchTerm) {
        const index = filteredIcons.findIndex(({ displayKey }) => displayKey === activeDisplayKey)
        setActiveIndex(index)
      }
    }, [activeDisplayKey, filteredIcons, searchTerm])

    return (
      <SelectInputContainer
        $small={false}
        fullWidth={fullWidth}
        className={className}
        aria-label={ariaLabel}
      >
        <SelectWrapper>
          <Popover open={isOpen} onOpenChange={onOpenChange}>
            <IconPopoverTrigger
              className={SELECT_TRIGGER_CLASSNAME}
              aria-invalid={isInvalid}
              aria-label={ariaLabel}
              $invalid={isInvalid}
              disabled={disabled}
              onBlur={setDidBlur}
              ref={forwardedRef}
              key="trigger"
              id={id}
            >
              <ValueWrapper>
                <Icon
                  subjectDisplayKey={activeDisplayKey ?? placeholderDisplayKey}
                  className={activeDisplayKey ? "" : "placeholder"}
                />
              </ValueWrapper>

              {!disabled && (
                <IconWrapper aria-hidden $open={isOpen}>
                  <SvgChevronDown />
                </IconWrapper>
              )}
            </IconPopoverTrigger>

            <IconPopoverContent showCloseButton={false} onKeyDown={onKeyDown} align="start">
              <Input
                small
                placeholder="Find icon…"
                value={searchTerm}
                onChange={onInputChange}
                tabIndex={0}
                autoFocus
              />

              {filteredIcons.length === 0 && <NoResultsMessage>No results found</NoResultsMessage>}

              <IconGrid>
                {filteredIcons.map(({ displayKey }, index) => (
                  <IconOption
                    key={displayKey}
                    displayKey={displayKey}
                    onIconSelect={onIconSelect}
                    selected={displayKey === activeDisplayKey}
                    current={activeIndex === index}
                  />
                ))}
              </IconGrid>
            </IconPopoverContent>
          </Popover>
        </SelectWrapper>
      </SelectInputContainer>
    )
  }
)

export const IconOption: React.FC<IconOptionProps> = ({
  displayKey,
  onIconSelect,
  selected,
  current,
}) => {
  const ref = React.useRef<HTMLLIElement | null>(null)

  const onClick = React.useCallback(() => {
    onIconSelect(displayKey)
  }, [onIconSelect, displayKey])

  // If highlighted, ensure the selected icon is visible and not clipped
  React.useEffect(() => {
    if (current && ref.current) {
      const parent = ref.current.parentElement
      if (!parent) return

      const itemRect = ref.current.getBoundingClientRect()
      const parentRect = parent.getBoundingClientRect()

      if (itemRect.top < parentRect.top + ICON_GRID_GAP) {
        // If item is clipped at the top
        parent.scrollBy({
          top: itemRect.top - parentRect.top - ICON_GRID_GAP,
          behavior: "smooth",
        })
      } else if (itemRect.bottom > parentRect.bottom - ICON_GRID_GAP) {
        // If item is clipped at the bottom
        parent.scrollBy({
          top: itemRect.bottom - parentRect.bottom + ICON_GRID_GAP,
          behavior: "smooth",
        })
      }
    }
  }, [current])

  return (
    <StyledIconItem
      ref={ref}
      role="option"
      aria-label={displayKey}
      aria-selected={selected}
      aria-current={current}
      onClick={onClick}
    >
      <Icon subjectDisplayKey={displayKey} />
    </StyledIconItem>
  )
}
